/**
 * useLegalEntity — Hook per caricare i dati del titolare del trattamento
 * Usato nelle pagine pubbliche (Privacy Policy, Terms, Cookie Policy)
 * I dati vengono letti dalla tabella legal_entity (RLS: public read)
 * Così se cambia l'azienda, basta aggiornare da admin panel.
 */

import { useState, useEffect } from 'react';
import { supabase } from '@deskbay/shared/services/supabase/client';

export interface LegalEntityPublic {
    company_name: string;
    legal_form: string;
    tax_id: string;
    tax_id_type: string;
    country: string;
    address_street: string;
    address_zip: string;
    address_city: string;
    address_province: string;
    address_country: string;
    email_info: string;
    email_dpo: string | null;
    phone: string | null;
    website: string;
    representative_name: string;
    representative_role: string;
}

/** Valori di fallback se il DB non è raggiungibile */
const FALLBACK: LegalEntityPublic = {
    company_name: 'Deskbay de Daniele Pisu',
    legal_form: 'Autónomo',
    tax_id: 'Z1872544G',
    tax_id_type: 'CIF',
    country: 'ES',
    address_street: 'Calle Tinguafaya, 90C, 2AA',
    address_zip: '38652',
    address_city: 'Arona',
    address_province: 'S.C. de Tenerife',
    address_country: 'Spagna',
    email_info: 'info@deskbay.io',
    email_dpo: null,
    phone: null,
    website: 'https://app.deskbay.io',
    representative_name: 'Daniele Pisu',
    representative_role: 'Titolare',
};

export function useLegalEntity() {
    const [entity, setEntity] = useState<LegalEntityPublic>(FALLBACK);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data, error } = await supabase
                    .from('legal_entity')
                    .select('company_name, legal_form, tax_id, tax_id_type, country, address_street, address_zip, address_city, address_province, address_country, email_info, email_dpo, phone, website, representative_name, representative_role')
                    .limit(1)
                    .single();
                if (!cancelled && data && !error) {
                    setEntity(data);
                }
            } catch (e) {
                // Fallback silenzioso — usa i valori hardcoded
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    /** Indirizzo completo formattato */
    const fullAddress = `${entity.address_street}, ${entity.address_zip} — ${entity.address_city} (${entity.address_province}), ${entity.address_country}`;

    /** Riga fiscale: "C.I.F.: Z1872544G" */
    const taxLine = `${entity.tax_id_type}: ${entity.tax_id}`;

    return { entity, loading, fullAddress, taxLine };
}
